import React, { useCallback } from "/vendor/react";
import gql from "/vendor/graphql-tag";

import { ApolloError } from "/vendor/apollo-client";
import { isUnreachable } from "/lib/util/fetchError";
import { PollingDuration } from "/lib/constant";

import {
  parseIntParam,
  parseStringParam,
  parseArrayParam,
} from "/lib/util/params";
import {
  useApolloClient,
  useBreakpoint,
  useConfigurationProvider,
  useFilterParams,
  useSearchParams,
  useQuery,
  useRouter,
  UseQueryResult,
} from "/lib/component/util";
import { MobileFullWidthContent, Content, Loader } from "/lib/component/base";
import {
  AppLayout,
  BoundFilterList,
  EventsList,
  EventsListToolbar,
  NotFound,
} from "/lib/component/partial";

import createSilence from "/lib/mutation/createSilence";
import deleteSilence from "/lib/mutation/deleteSilence";
import resolveEvent from "/lib/mutation/resolveEvent";
import executeCheck from "/lib/mutation/executeCheck";
import deleteEvent from "/lib/mutation/deleteEvent";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useDashboardViewData } from "./Dashboard";

const statusQueryMap = {
  Failing: 'fieldSelector: event.check.status notin ["0", "1", "2"]',
  Warning: 'fieldSelector: event.check.status == "1"',
  Critical: 'fieldSelector: event.check.status == "2"',
  Incident: 'fieldSelector: event.check.status notin ["0"]',
  Passing: 'fieldSelector: event.check.status == "0"',
  Unknown: 'fieldSelector: event.check.status notin ["0", "1", "2"]',
};

const useStyles = makeStyles({
  table: {
    // backgroundColor: '#424242',
    fontFamily: "Arial",
  },
  tableCell: {
    color: "white",
  },
  printerTable: {
    height: "100%",
  },
});

interface Variables {
  namespace: string;
  filters: string[];
  order: string;
  limit: number;
  offset: number;
}

interface Props {
  toolbarContent?: React.ReactNode;
  toolbarItems?: React.ReactNode;
  query: UseQueryResult<any, any>;
  variables: Variables;
  onCreateSilence: (_: any) => void;
  onDeleteSilence: (_: any) => void;
  onResolve: (_: any) => Promise<any>;
  onExecute: (_: any) => Promise<any>;
  onDelete: (_: any) => Promise<any>;
}

interface EventsPieProps extends Props {
  entitiesQuery: UseQueryResult<any, any>;
}

interface EventsViewDashContentProps extends Props {
  passingQuery: UseQueryResult<any, any>;
}

export const useEventsViewQueryVariables = (): Variables => {
  const [params] = useSearchParams();
  const config = useConfigurationProvider();

  const limit = parseIntParam(params.limit, config.preferences.pageSize || 25);
  const offset = parseIntParam(params.offset, 0);
  const order = parseStringParam(params.order, "LASTOK");
  const filters = parseArrayParam(params.filters);

  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );

  return {
    namespace,
    limit,
    offset,
    order,
    filters,
  };
};

const useEntitiesDashViewQueryVariables = (): Variables => {
  const [params] = useSearchParams();
  const config = useConfigurationProvider();
  const limit = parseIntParam(params.limit, config.preferences.pageSize);
  const offset = parseIntParam(params.offset, 0);
  const order = parseStringParam(params.order, "ID");
  const filters = ["class:agent"];

  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );

  return {
    namespace,
    limit,
    offset,
    order,
    filters,
  };
};

export const useEventsDashViewQueryVariables = (
  status: string,
  check?: string,
): Variables => {
  const [params] = useSearchParams();

  const limit = 0;
  const offset = parseIntParam(params.offset, 0);
  const order = parseStringParam(params.order, "LASTOK");
  const filters = check
    ? [`${statusQueryMap[status]}`, `check:${check}`]
    : [statusQueryMap[status]];

  const router = useRouter();
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    "",
  );

  return {
    namespace,
    limit,
    offset,
    order,
    filters,
  };
};

export const eventsViewFragments = {
  namespace: gql`
    fragment EventsView_namespace on Namespace {
      id
      ...EventsList_namespace
    }

    ${EventsList.fragments.namespace}
  `,
};

const eventsViewQuery = gql`
  query EventsViewQuery(
    $namespace: String!
    $filters: [String!]
    $order: EventsListOrder
    $limit: Int
    $offset: Int
  ) {
    namespace(name: $namespace) {
      id
      ...EventsView_namespace
    }
  }

  ${eventsViewFragments.namespace}
`;

const eventsDashViewQuery = gql`
  query EventsViewQuery(
    $namespace: String!
    $filters: [String!]
    $order: EventsListOrder
    $limit: Int
    $offset: Int
  ) {
    namespace(name: $namespace) {
      id
      name
      events(
        limit: $limit
        offset: $offset
        filters: $filters
        orderBy: $order
      ) @connection(key: "events", filter: ["filters", "orderBy"]) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

const entitiesDashViewQuery = gql`
  query EntitiesViewQuery(
    $namespace: String!
    $limit: Int
    $offset: Int
    $order: EntityListOrder
    $filters: [String!]
  ) {
    namespace(name: $namespace) {
      id
      entities(
        limit: $limit
        offset: $offset
        filters: $filters
        orderBy: $order
      ) @connection(key: "entities", filter: ["filters", "orderBy"]) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

export const EventsViewContent = ({
  query,
  toolbarContent,
  toolbarItems,
  variables,
  ...props
}: Props) => {
  const client = useApolloClient();
  const [, setFilters] = useFilterParams();
  const [, setParams] = useSearchParams();
  const isSmViewport = !useBreakpoint("sm", "gt");

  const { data = {}, networkStatus, aborted, refetch } = query;
  const { namespace } = data;

  // see: https://github.com/apollographql/apollo-client/blob/master/packages/apollo-client/src/core/networkStatus.ts
  const loading = networkStatus < 6;

  const onClickReset = useCallback(
    () =>
      setParams((params) => ({
        ...params,
        filters: undefined,
        order: undefined,
      })),
    [setParams],
  );

  if (!data.namespace && !loading && !aborted) {
    return (
      <AppLayout>
        <NotFound />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <Content marginBottom>
          <EventsListToolbar
            onClickReset={onClickReset}
            toolbarContent={toolbarContent}
            toolbarItems={toolbarItems}
          />
        </Content>
        <MobileFullWidthContent>
          <EventsList
            client={client}
            editable={!isSmViewport}
            limit={variables.limit}
            offset={variables.offset}
            filters={variables.filters}
            onChangeQuery={setParams}
            onChangeFilters={setFilters}
            namespace={namespace}
            loading={(loading && !namespace) || aborted}
            refetch={refetch}
            {...props}
          />
        </MobileFullWidthContent>
      </div>
    </AppLayout>
  );
};

type TableTypes = EventsViewDashContentProps & {
  warningQuery: UseQueryResult<any, any>;
  failingQuery: UseQueryResult<any, any>;
  criticalQuery: UseQueryResult<any, any>;
};

export const EventsDashViewContent = ({
  query,
  failingQuery,
  criticalQuery,
  warningQuery,
  passingQuery,
}: TableTypes) => {
  const { data = {}, networkStatus, aborted } = query;
  const {
    data: passingData = {},
    networkStatus: passingNetStat,
    aborted: passingAborted,
  } = passingQuery;
  const {
    data: warningData = {},
    networkStatus: warningNetStat,
    aborted: warningAborted,
  } = warningQuery;
  const {
    data: criticalData = {},
    networkStatus: criticalNetStat,
    aborted: criticalAborted,
  } = criticalQuery;
  const {
    data: failingData = {},
    networkStatus: failingNetStat,
    aborted: failingAborted,
  } = failingQuery;

  const loading =
    networkStatus < 6 ||
    passingNetStat < 6 ||
    warningNetStat < 6 ||
    criticalNetStat < 6 ||
    failingNetStat < 6;

  const classes = useStyles();
  const theme = useTheme();

  if (
    !data.namespace &&
    !loading &&
    !aborted &&
    !passingAborted &&
    !warningAborted &&
    !criticalAborted &&
    !failingAborted
  ) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  const totalEvents = () => {
    return (
      passingData.namespace.events.pageInfo.totalCount +
        data.namespace.events.pageInfo.totalCount || 0
    );
  };

  const failingEvents = () => {
    return failingData.namespace.events.pageInfo.totalCount || 0;
  };

  const passingEvents = () => {
    return passingData.namespace.events.pageInfo.totalCount || 0;
  };

  const warningEvents = () => {
    return warningData.namespace.events.pageInfo.totalCount || 0;
  };

  const criticalEvents = () => {
    return criticalData.namespace.events.pageInfo.totalCount || 0;
  };

  return (
    <div>
      <Content>
        <TableContainer
          component={Paper}
          className={classes.table}
          style={{ backgroundColor: theme.palette.background.paper }}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>Checks</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>
                  Total Checks
                </TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {totalEvents()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>Passing</TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {passingEvents()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>Failing</TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {failingEvents()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>Warning</TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {warningEvents()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={theme.palette.text.primary}>Critical</TableCell>
                <TableCell className={theme.palette.text.primary}>
                  {criticalEvents()}
                </TableCell>
              </TableRow>
              {/* <TableRow>
                <TableCell className={theme.palette.text.primary}>Other</TableCell>
                <TableCell className={theme.palette.text.primary}>{0}</TableCell>
              </TableRow> */}
            </TableBody>
          </Table>
        </TableContainer>
      </Content>
    </div>
  );
};

export const EventsDashViewIncidentChartContent = ({
  query,
  passingQuery,
}: EventsViewDashContentProps) => {
  const { data = {}, networkStatus, aborted } = query;
  const {
    data: passingData = {},
    networkStatus: passingNetStat,
    aborted: passingAborted,
  } = passingQuery;

  const loading = networkStatus < 6 || passingNetStat < 6;

  const router = useRouter();
  const theme = useTheme();

  const [, setParams] = useSearchParams();

  if (!data.namespace && !loading && !aborted && !passingAborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  function redirectToEvents() {
    router.history.push(`/${data.namespace.name}/events`);
    setTimeout(() => {
      setParams(() => ({
        filters: [statusQueryMap["Incident"]],
      }));
    }, 1);
  }

  function redirectToPassingEvents() {
    router.history.push(`/${data.namespace.name}/events`);
  }

  const failingEvents = () => {
    return data.namespace.events.pageInfo.totalCount || 0;
  };

  const passingEvents = () => {
    return passingData.namespace.events.pageInfo.totalCount || 0;
  };

  const passingFontSize =
    passingEvents().toString().length >= 4 ? "72px" : "96px";
  const failingFontSize =
    failingEvents().toString().length >= 4 ? "72px" : "96px";

  const getBackgroundColor = () => {
    const incidentCount = failingEvents();
    return incidentCount === 0
      ? theme.palette.success.main
      : theme.palette.error.main;
  };
  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Content>
        <div
          style={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <Paper
            style={{
              backgroundColor: theme.palette.success.main,
              height: "155px",
              marginBottom: "10px",
              width: "100%",
            }}
            onClick={redirectToPassingEvents}
          >
            <Box
              style={{
                height: "120px",
                width: "auto",
                display: "flex",
                flexGrow: 1,
                padding: "8px",
                justifyContent: "center",
                alignItems: "center",
                fontSize: passingFontSize,
                fontFamily: "monospace",
                color: "white",
              }}
            >
              {passingEvents()}
            </Box>
            <Box
              style={{
                fontFamily: "Arial",
                fontSize: "22px",
                textAlign: "center",
                marginBottom: "20px",
                color: "white",
              }}
            >
              Passing
            </Box>
          </Paper>
          <Paper
            style={{
              backgroundColor: getBackgroundColor(),
              height: "155px",
              width: "100%",
            }}
            onClick={redirectToEvents}
          >
            <Box
              style={{
                height: "120px",
                width: "auto",
                display: "flex",
                flexGrow: 1,
                padding: "8px",
                justifyContent: "center",
                alignItems: "center",
                fontSize: failingFontSize,
                fontFamily: "monospace",
                color: "white",
              }}
            >
              {failingEvents()}
            </Box>
            <Box
              style={{
                fontFamily: "Arial",
                fontSize: "22px",
                textAlign: "center",
                marginBottom: "20px",
                color: "white",
              }}
            >
              Issues
            </Box>
          </Paper>
        </div>
      </Content>
    </div>
  );
};

export const EventsCallAttendantChart = ({ query }: Props) => {
  const { data = {}, networkStatus, aborted } = query;

  const loading = networkStatus < 6;

  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();

  const [, setParams] = useSearchParams();

  if (!data.namespace && !loading && !aborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  function redirectToEvents() {
    router.history.push(`/${data.namespace.name}/events`);
    setTimeout(() => {
      setParams(() => ({
        filters: [statusQueryMap["Incident"], "check:call_attendant"],
      }));
    }, 1);
  }

  const failingEvents = () => {
    return data.namespace.events.pageInfo.totalCount || 0;
  };

  const getBackgroundColor = () => {
    const incidentCount = failingEvents();
    return incidentCount === 0
      ? theme.palette.success.main
      : theme.palette.error.main;
  };
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div style={{ height: "100%", width: "100%" }} onClick={redirectToEvents}>
      <Content className={classes.printerTable}>
        <Paper
          style={{
            backgroundColor: getBackgroundColor(),
            height: "100%",
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <Box
            style={{
              height: "80%",
              width: "auto",
              display: "flex",
              flexGrow: 1,
              padding: "8px",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "96px",
              fontFamily: "monospace",
              color: "white",
            }}
          >
            {failingEvents()}
          </Box>
          <Box
            style={{
              fontFamily: "Arial",
              fontSize: "16px",
              textAlign: "center",
              paddingBottom: "20px",
              paddingTop: "10px",
              color: "white",
            }}
          >
            Call Attendant Incidents
          </Box>
        </Paper>
      </Content>
    </div>
  );
};

export const EventsPrinterChart = ({ query }: Props) => {
  const { data = {}, networkStatus, aborted } = query;

  const loading = networkStatus < 6;

  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();

  const [, setParams] = useSearchParams();

  if (!data.namespace && !loading && !aborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  function redirectToEvents() {
    router.history.push(`/${data.namespace.name}/events`);
    setTimeout(() => {
      setParams(() => ({
        filters: [statusQueryMap["Incident"], "check:printer"],
      }));
    }, 1);
  }

  const failingEvents = () => {
    return data.namespace.events.pageInfo.totalCount || 0;
  };
  const getBackgroundColor = () => {
    const incidentCount = failingEvents();
    return incidentCount === 0
      ? theme.palette.success.main
      : theme.palette.warning.main;
  };
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div style={{ height: "100%", width: "100%" }} onClick={redirectToEvents}>
      <Content className={classes.printerTable}>
        <Paper
          style={{
            backgroundColor: getBackgroundColor(),
            height: "100%",
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <Box
            style={{
              height: "80%",
              width: "auto",
              display: "flex",
              flexGrow: 1,
              padding: "8px",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "96px",
              fontFamily: "monospace",
              color: "white",
            }}
          >
            {failingEvents()}
          </Box>
          <Box
            style={{
              fontFamily: "Arial",
              fontSize: "16px",
              textAlign: "center",
              paddingBottom: "20px",
              paddingTop: "10px",
              color: "white",
            }}
          >
            Printer Issues
          </Box>
        </Paper>
      </Content>
    </div>
  );
};

export const BNAChart = ({ query }: Props) => {
  const { data = {}, networkStatus, aborted } = query;

  const loading = networkStatus < 6;

  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();

  const [, setParams] = useSearchParams();

  if (!data.namespace && !loading && !aborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  function redirectToEvents() {
    router.history.push(`/${data.namespace.name}/events`);
    setTimeout(() => {
      setParams(() => ({
        filters: [statusQueryMap["Incident"], "check:bna-check"],
      }));
    }, 1);
  }

  const failingEvents = () => {
    return data.namespace.events.pageInfo.totalCount || 0;
  };
  const getBackgroundColor = () => {
    const incidentCount = failingEvents();
    return incidentCount === 0
      ? theme.palette.success.main
      : theme.palette.warning.main;
  };
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div style={{ height: "100%", width: "100%" }} onClick={redirectToEvents}>
      <Content className={classes.printerTable}>
        <Paper
          style={{
            backgroundColor: getBackgroundColor(),
            height: "100%",
            width: "100%",
          }}
        >
          <Box
            style={{
              height: "80%",
              width: "auto",
              display: "flex",
              flexGrow: 1,
              padding: "8px",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "96px",
              fontFamily: "monospace",
              color: "white",
            }}
          >
            {failingEvents()}
          </Box>
          <Box
            style={{
              fontFamily: "Arial",
              fontSize: "16px",
              textAlign: "center",
              paddingBottom: "20px",
              paddingTop: "10px",
              color: "white",
            }}
          >
            BNA Issues
          </Box>
        </Paper>
      </Content>
    </div>
  );
};

type BarChartTypes = EventsViewDashContentProps & {
  warningQuery: UseQueryResult<any, any>;
  failingQuery: UseQueryResult<any, any>;
  criticalQuery: UseQueryResult<any, any>;
};

export const EventsBarChartDashViewContent = ({
  failingQuery,
  criticalQuery,
  warningQuery,
}: BarChartTypes) => {
  const { data = {}, networkStatus, aborted } = failingQuery;
  const {
    data: warningData = {},
    networkStatus: warningNetStat,
    aborted: warningAborted,
  } = warningQuery;
  const {
    data: criticalData = {},
    networkStatus: criticalNetStat,
    aborted: criticalAborted,
  } = criticalQuery;
  const router = useRouter();
  const theme = useTheme();

  const [, setParams] = useSearchParams();
  const loading =
    networkStatus < 6 || warningNetStat < 6 || criticalNetStat < 6;

  if (
    !data.namespace &&
    !loading &&
    !aborted &&
    !warningAborted &&
    !criticalAborted
  ) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return <Loader loading={loading} />;
  }

  const failingEvents = () => {
    return data.namespace.events.pageInfo.totalCount || 0;
  };

  const warningEvents = () => {
    return warningData.namespace.events.pageInfo.totalCount || 0;
  };

  const criticalEvents = () => {
    return criticalData.namespace.events.pageInfo.totalCount || 0;
  };

  const handleClick = (e: any) => {
    router.history.push(`/${data.namespace.name}/events`);
    setTimeout(() => {
      setParams(() => ({
        filters: [`${statusQueryMap[e.name]}`],
      }));
    }, 1);
  };

  const barData: any = [
    {
      name: "Critical",
      events: criticalEvents(),
    },
    {
      name: "Warning",
      events: warningEvents(),
    },
    {
      name: "Failing",
      events: failingEvents(),
    },
  ];

  const colors = {
    Warning: theme.palette.warning.main,
    Passing: theme.palette.success.main,
    Failing: theme.palette.error.main,
    Critical: theme.palette.error.main,
  };

  return (
    <Content>
      <Paper
        style={{
          backgroundColor: theme.palette.background.paper,
          height: "auto",
          width: "100%",
        }}
      >
        <ResponsiveContainer aspect={2}>
          <BarChart
            data={barData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tick={{ fill: "white" }}
              tickLine={{ stroke: "white" }}
              style={{ fontFamily: "Arial" }}
            />
            <YAxis
              tick={{ fill: "white" }}
              tickLine={{ stroke: "white" }}
              style={{ fontFamily: "Arial" }}
            />
            <Bar
              onClick={handleClick}
              dataKey="events"
              fill="Green"
              label={{ position: "inside", fill: "black", fontFamily: "Arial" }}
              isAnimationActive={false}
            >
              {barData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={colors[entry.name]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div
          style={{ textAlign: "center", fontFamily: "Arial", color: "white" }}
        >
          ISSUE STATUSES
        </div>
      </Paper>
    </Content>
  );
};

export const EventsPieChartDashContent = ({
  query,
  entitiesQuery,
}: EventsPieProps) => {
  const { data = {}, networkStatus, aborted } = query;
  const {
    data: entitiesData = {},
    networkStatus: entitiesNetStat,
    aborted: entitiesAborted,
  } = entitiesQuery;
  const loading = networkStatus < 6 || entitiesNetStat < 6;
  const theme = useTheme();
  if (!data.namespace && !loading && !aborted && !entitiesAborted) {
    return (
      <Content>
        <NotFound />
      </Content>
    );
  }

  if (loading || aborted) {
    return (
      <Content>
        <Loader loading={loading} />
      </Content>
    );
  }

  function getTotalKeepalives() {
    return entitiesData.namespace.entities.pageInfo.totalCount || 0;
  }

  function getUnresponsiveKeepalives() {
    const agents = data.namespace.events.nodes.map((a: any) => {
      return a.check;
    });
    return (
      agents.filter((a: any) => a.name === "keepalive" && a.state !== "passing")
        .length || 0
    );
  }

  const pieData: any = [
    {
      name: "Total",
      value: getTotalKeepalives(),
    },
    {
      name: "Unresponsive",
      value: getUnresponsiveKeepalives(),
    },
  ];

  const COLORS = [theme.palette.success.main, theme.palette.error.main];

  const renderCustomizedLabel = ({ percent, index }: any) => {
    const x = "30%";
    const y = "63%";

    if (index === 1) {
      return;
    }

    return (
      <text
        fontFamily="Arial"
        fontSize="70px"
        x={x}
        y={y}
        fill={theme.palette.text.primary}
        textAnchor={"center"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Content>
      <Paper
        style={{
          backgroundColor: theme.palette.background.paper,
          height: "auto",
          width: "100%",
          paddingBottom: "8px",
        }}
      >
        <ResponsiveContainer aspect={1}>
          <PieChart>
            <Pie
              cy="70%"
              dataKey="value"
              startAngle={180}
              endAngle={0}
              data={pieData}
              outerRadius={150}
              innerRadius={100}
              fill="#8884d8"
              label={renderCustomizedLabel}
              labelLine={false}
              isAnimationActive={false}
            >
              {pieData.map((entry: any, index: number) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{ textAlign: "center", fontFamily: "Arial", color: theme.palette.text.primary }}
        >
          RESPONSIVE AGENTS (PCT.)
        </div>
      </Paper>
    </Content>
  );
};

EventsViewContent.defaultProps = {
  toolbarContent: <BoundFilterList />,
};

export const EventsView = () => {
  const client = useApolloClient();
  const onCreateSilence = React.useCallback(
    (vars) => createSilence(client, vars),
    [client],
  );
  const onDeleteSilence = React.useCallback(
    (vars) => deleteSilence(client, vars),
    [client],
  );
  const onResolve = React.useCallback((vars) => resolveEvent(client, vars), [
    client,
  ]);
  const onExecute = React.useCallback((vars) => executeCheck(client, vars), [
    client,
  ]);
  const onDelete = React.useCallback((vars) => deleteEvent(client, vars), [
    client,
  ]);

  const variables = useEventsViewQueryVariables();
  const query = useQuery({
    query: eventsViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  return (
    <EventsViewContent
      query={query}
      variables={variables}
      onCreateSilence={onCreateSilence}
      onDeleteSilence={onDeleteSilence}
      onResolve={onResolve}
      onExecute={onExecute}
      onDelete={onDelete}
    />
  );
};

interface DashViewProps {
  type:
    | "table"
    | "incidentChart"
    | "barChart"
    | "pieChart"
    | "printerChart"
    | "bnaChart"
    | "callAttendantChart";
  data?: any;
}

export const EventsDashView = ({ type, data }: DashViewProps) => {
  const {
    onCreateSilence,
    onDeleteSilence,
    onResolve,
    onExecute,
    onDelete,
    failingVariables,
    query,
    failingQuery,
    passingQuery,
    warningQuery,
    criticalQuery,
    eventsQuery,
    entitiesQuery,
    printerVariables,
    printerQuery,
    callAttendantQuery,
    bnaQuery,
  } = data;

  if (type === "table") {
    return (
      <EventsDashViewContent
        query={query}
        failingQuery={failingQuery}
        criticalQuery={criticalQuery}
        warningQuery={warningQuery}
        passingQuery={passingQuery}
        variables={failingVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  if (type === "incidentChart") {
    return (
      <EventsDashViewIncidentChartContent
        query={query}
        passingQuery={passingQuery}
        variables={failingVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  if (type === "barChart") {
    return (
      <EventsBarChartDashViewContent
        query={query}
        failingQuery={failingQuery}
        criticalQuery={criticalQuery}
        warningQuery={warningQuery}
        variables={failingVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
        passingQuery={query}
      />
    );
  }

  if (type === "pieChart") {
    return (
      <EventsPieChartDashContent
        query={eventsQuery}
        entitiesQuery={entitiesQuery}
        variables={failingVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  if (type === "printerChart") {
    return (
      <EventsPrinterChart
        query={printerQuery}
        variables={printerVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  if (type === "callAttendantChart") {
    return (
      <EventsCallAttendantChart
        query={callAttendantQuery}
        variables={printerVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  if (type === "bnaChart") {
    return (
      <BNAChart
        query={bnaQuery}
        variables={printerVariables}
        onCreateSilence={onCreateSilence}
        onDeleteSilence={onDeleteSilence}
        onResolve={onResolve}
        onExecute={onExecute}
        onDelete={onDelete}
      />
    );
  }

  return (
    <EventsDashViewContent
      query={query}
      failingQuery={failingQuery}
      criticalQuery={criticalQuery}
      warningQuery={warningQuery}
      passingQuery={passingQuery}
      variables={failingVariables}
      onCreateSilence={onCreateSilence}
      onDeleteSilence={onDeleteSilence}
      onResolve={onResolve}
      onExecute={onExecute}
      onDelete={onDelete}
    />
  );
};
export { EventsList };
